/** @jsxImportSource @emotion/react */
import { useTheme, Container, Flex, Link, Message, Stack, Text } from "basis";
import { useProducts } from "library/hooks/useProducts";
import { formatDueDate } from "library/utils";
import { formatAmount } from "library/moneyUtils";
import { Separator } from "../Separator/Separator";

function PaymentDueMessage({ paymentInfo }) {
  const amount = formatAmount(paymentInfo.minimum_monthly_repayment, {
    withCents: true,
  });

  return (
    <Message
      severity="info-or-minor"
      bg="secondary.lightBlue.t25"
      callToAction={
        <Link
          appearance="secondary-button"
          href="/ways-to-pay"
          newTab={false}
          state={{ paymentInfo }}
        >
          Make a payment
        </Link>
      }
    >
      {paymentInfo.amount_immediate_due > 0 ? (
        <>
          {`Minimum payment of {amount} is `}
          <strong>overdue</strong>
        </>
      ) : (
        <>
          {`Minimum payment of ${amount} is due by `}
          <strong>{formatDueDate(paymentInfo.payment_due)}</strong>
        </>
      )}
    </Message>
  );
}

function AccountCard(props) {
  const theme = useTheme();
  const {
    accountId,
    productId,
    linked_card_last4: linkedCardLast4,
    current_balance: currentBalance,
    available_balance: availableBalance,
    credit_limit: creditLimit,
    payment_due_date: paymentDueDate,
    payment_due: paymentDue,
    accountNumber,
    last_statement: lastStatement,
    minimum_monthly_repayment: minimumMonthlyRepayment,
    amount_immediate_due: amountImmediateDue,
    payin = {},
  } = props;
  const productsInfo = useProducts();
  const products = productsInfo.data ?? [];
  const product = products.find((p) => p.id === productId);
  const paymentInfo = {
    accountId,
    accountNumber,
    productId,
    minimum_monthly_repayment: minimumMonthlyRepayment,
    current_balance: currentBalance,
    amount_immediate_due: amountImmediateDue,
    payment_due_date: paymentDueDate,
    payment_due: paymentDue,
    last_statement: lastStatement,
    payinEligible: payin.status === "ELIGIBLE",
    accountNum: payin.bank_account,
    bsb: payin.bsb,
    lastFour: linkedCardLast4,
    creditLimit,
  };

  return (
    <>
      <div
        css={{
          boxShadow: "1px 1px 4px 1px rgba(126, 126, 126, 0.3)",
        }}
        data-testid={`credit-card-ending-${linkedCardLast4}-info`}
      >
        <Flex direction="column" direction-md="row">
          <Container width="100%" width-md="40%">
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 108,
                padding: theme.space[5],
                backgroundColor: theme.getColor("primary.blue.t100"),
                color: theme.getColor("white"),
              }}
            >
              {product && (
                <Text textStyle="heading5" color="white" margin="0 0 2 0">
                  {product.name}
                </Text>
              )}
              <Text textStyle="body2" color="white" margin="auto 0 0 0">
                Card ending {linkedCardLast4}
              </Text>
            </div>
          </Container>
          <Container width="100%" width-md="60%" padding="5">
            <Stack gap="4">
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Text>Credit limit</Text>
                <Text>
                  <strong>
                    {formatAmount(creditLimit, { withCents: true })}
                  </strong>
                </Text>
              </div>
              <Separator />
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Text>Current balance</Text>
                <Text>
                  <strong>
                    {formatAmount(currentBalance, { withCents: true })}
                  </strong>
                </Text>
              </div>
              <Separator />
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Text>Available to spend</Text>
                <Text>
                  <strong>
                    {formatAmount(availableBalance, { withCents: true })}
                  </strong>
                </Text>
              </div>
            </Stack>
          </Container>
        </Flex>
      </div>
      <Container
        margin="4 0 0 0"
        testId={`credit-card-ending-${linkedCardLast4}-payment-message`}
      >
        {paymentInfo.amount_immediate_due > 0 ? (
          <Message
            severity="info-or-minor"
            bg="secondary.lightBlue.t25"
            callToAction={
              <Link
                appearance="secondary-button"
                href="/ways-to-pay"
                newTab={false}
                state={{ paymentInfo }}
              >
                Make a payment
              </Link>
            }
          >
            You have an overdue payment.
          </Message>
        ) : paymentInfo.current_balance <=
          0 ? null : paymentInfo.minimum_monthly_repayment === 0 ? (
          <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
            No minimum payment required at this time.
          </Message>
        ) : (
          <PaymentDueMessage paymentInfo={paymentInfo} />
        )}
      </Container>
    </>
  );
}
export { AccountCard };
